import { Link } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { ArrowRight, Code, Briefcase, Heart, Zap, Users, GraduationCap, UserCheck } from "lucide-react";

const perks = [
  { icon: Zap, title: "Latest Tech Stack", desc: "Work with cutting-edge technologies on diverse projects." },
  { icon: Users, title: "Small, Impactful Team", desc: "Your work directly impacts the company and our clients." },
  { icon: Heart, title: "Work-Life Balance", desc: "Flexible hours and remote-friendly environment." },
  { icon: Briefcase, title: "Growth Opportunities", desc: "Clear career paths with mentorship from leadership." },
  { icon: GraduationCap, title: "Learning Budget", desc: "Annual budget for courses, conferences, and certifications." },
  { icon: Code, title: "Open Source Time", desc: "Dedicated time for personal projects and open source contributions." },
];

export default function Careers() {
  return (
    <div data-testid="careers-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-indigo-600/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-indigo-500/10 text-indigo-400 border-indigo-500/20 hover:bg-indigo-500/10 text-xs font-mono" data-testid="careers-badge">
              CAREERS
            </Badge>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-50 leading-[1.1] tracking-tight">
              Build the future{" "}
              <span className="gradient-text">with us.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg text-slate-400 leading-relaxed max-w-xl">
              Join a team of passionate engineers and designers at the forefront of 
              software development. We're always looking for talented people.
            </p>
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="relative py-24 lg:py-32 bg-slate-900/30" data-testid="perks-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
              Why you'll love <span className="gradient-text">working here.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {perks.map((perk, i) => (
              <div key={i} data-testid={`perk-${i}`} className="p-6 rounded-xl bg-slate-900/50 border border-slate-800 hover:border-indigo-500/20 transition-all group">
                <perk.icon className="w-6 h-6 text-indigo-400 mb-4 group-hover:text-indigo-300 transition-colors" />
                <h3 className="font-['Outfit'] font-semibold text-slate-200 mb-2">{perk.title}</h3>
                <p className="text-slate-400 text-xs leading-relaxed">{perk.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team at Capacity */}
      <section className="relative py-24 lg:py-32" data-testid="positions-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="p-10 lg:p-14 rounded-2xl bg-slate-900/50 border border-slate-800">
            <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center mx-auto mb-6">
              <UserCheck className="w-8 h-8 text-indigo-400" />
            </div>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-50 tracking-tight mb-4">
              We're at Full Capacity!
            </h2>
            <p className="text-slate-400 text-sm leading-relaxed max-w-lg mx-auto mb-3">
              Our team is currently operating at maximum strength and all positions 
              are filled. We're proud to have assembled an exceptional crew of 
              engineers and designers who are delivering outstanding work.
            </p>
            <p className="text-slate-400 text-sm leading-relaxed max-w-lg mx-auto mb-8">
              That said, great talent is always welcome. If you think you'd be a 
              perfect fit for Scriptovative, drop us your resume — we'll reach out 
              when the right opportunity opens up.
            </p>
            <Link
              to="/contact"
              data-testid="careers-send-resume"
              className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-3.5 rounded-full font-medium transition-all shadow-[0_0_20px_rgba(99,102,241,0.3)] hover:shadow-[0_0_30px_rgba(99,102,241,0.5)] hover:-translate-y-0.5"
            >
              Send Your Resume Anyway
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 lg:py-32">
        <div className="absolute inset-0 bg-gradient-to-t from-indigo-600/5 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
            Have something <span className="gradient-text">special?</span>
          </h2>
          <p className="mt-4 text-base text-slate-400 max-w-lg mx-auto">
            If you believe you can bring something extraordinary to our team, 
            we'd love to hear from you. Reach out and let's talk.
          </p>
          <Link
            to="/contact"
            data-testid="careers-cta"
            className="mt-8 inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-3.5 rounded-full font-medium transition-all shadow-[0_0_20px_rgba(99,102,241,0.3)] hover:shadow-[0_0_30px_rgba(99,102,241,0.5)] hover:-translate-y-0.5"
          >
            Contact Us
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
